import { useCompanyInboundSettingsQuery } from '@/modules/companies/queries/useCompanyInboundSettingsQuery';
import { useCompanyQuery } from '@/modules/companies/queries/useCompanyQuery';
import { useJobInboundSettingsQuery } from '@/modules/jobs/queries/useJobInboundSettingsQuery';
import type { InboundDraft } from '@/modules/jobs/signal/types';

export interface EditorInbound {
  initial: InboundDraft;
  isCareersPageEnabled: boolean;
}

/**
 * What the self-service job editor needs to show its inbound section, or `inbound: null` when it must not: inbound is
 * for Self-Service Tenants only (the API enforces it). Without `jobId` (create) the toggle starts at the
 * company's Careers Page setting, as the API would default it. `isLoading` holds the editor back, because it seeds its
 * drafts once on mount.
 */
export const useEditorInbound = (
  companyId: string,
  jobId?: string,
): { isLoading: boolean; inbound: EditorInbound | null } => {
  const companyQuery = useCompanyQuery(companyId, {
    enabled: Boolean(companyId),
  });
  const isAvailable = companyQuery.data?.isSelfServiceWorkspace === true;
  const companyInboundQuery = useCompanyInboundSettingsQuery(
    isAvailable ? companyId : undefined,
  );
  const jobInboundQuery = useJobInboundSettingsQuery(
    isAvailable ? jobId : undefined,
  );

  if (!companyId) {
    return { isLoading: false, inbound: null };
  }
  if (companyQuery.isPending) return { isLoading: true, inbound: null };
  if (!isAvailable) return { isLoading: false, inbound: null };
  if (companyInboundQuery.isPending || (jobId && jobInboundQuery.isPending)) {
    return { isLoading: true, inbound: null };
  }

  const companySettings = companyInboundQuery.data;
  const jobSettings = jobInboundQuery.data;
  // A failed read hides the section; the job still saves without touching its inbound settings.
  if (!companySettings || (jobId && !jobSettings)) {
    return { isLoading: false, inbound: null };
  }

  return {
    isLoading: false,
    inbound: {
      initial: jobSettings
        ? {
            isInboundEnabled: jobSettings.isInboundEnabled,
            publicJobDescription: jobSettings.publicJobDescription ?? '',
          }
        : {
            isInboundEnabled: companySettings.isCareersPageEnabled,
            publicJobDescription: '',
          },
      isCareersPageEnabled: companySettings.isCareersPageEnabled,
    },
  };
};
